import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import produce from 'immer';
import React, {useEffect, useState} from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {ReviewerType, ReviewLifecycleType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {GenericResponseJSON} from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  getSendForReviewMetadata,
  SEND_FOR_REVIEW_ENDED_EVENT,
  SEND_FOR_REVIEW_STARTED_EVENT,
} from 'performance/processReviewsEvents';
import EntityStore from 'stores/EntityStore';
import {FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import FormGroup from 'ui-library/bs/FormGroup';
import Label from 'ui-library/bs/Label';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {NEAR_BLACK} from 'ui-library/Colors';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import Modal from 'ui-library/modals/Modal';
import Stepper, {StepperStep, StepperStepProps, stepperStyles} from 'ui-library/stepper/Stepper';
import {TextAreaChangeEvent} from 'util/Types';

import {
  ReviewLifecycleStageQuery,
  ReviewLifecycleStageQueryVariables,
} from './__generated/ReviewLifecycleStageQuery';
import {EsignatureStatement} from './EsignatureStatement';
import ReviewProcessSelector, {
  generateReviewProcessSelectorOptions,
} from './review-lifecycles/ReviewProcessSelector';
import {ReviewComment} from './ReviewComment';
import {ReviewAction} from './ReviewConstants';
import {RLCStageReviewerData} from './ReviewDAO';
import {EntityByStageId, IsTeamReassignableByStageId} from './ReviewLifecycleSummaryPanel';
import {ReviewStageReviewerAssignmentRow} from './ReviewStageReviewerAssignmentRow';

type ReviewableGraphQL = ExcludeInaccessible<ReviewLifecycleStageQuery['reviewable']>;

export interface SendForReviewOnRequestSubmitArgs {
  comment: string;
  stageReviewers: Array<RLCStageReviewerData>;
}

interface BaseSendForReviewModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (result: SendForReviewOnRequestSubmitArgs) => Promise<void>;
  existingReviewerUsersByStage?: EntityByStageId;
  existingCanTeamReassignByStage?: IsTeamReassignableByStageId;
  defaultComment?: string;
  /** Custom rendering of additional info related to the review stage */
  renderAdditionalReviewInfo?: (fileId: string, reviewAction: ReviewAction) => JSX.Element | null;
  /** Custom error rendering, configured from ReviewBadge. */
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
}

export interface SendForReviewModalProps extends BaseSendForReviewModalProps {
  reviewableId: string;
  performanceMetadata?: {[key: string]: string};
}

interface PresentationalSendForReviewModalProps extends SendForReviewModalProps {
  reviewable: ReviewableGraphQL;
}

export const REVIEW_LIFECYCLE_STAGE_QUERY = gql`
  query ReviewLifecycleStageQuery($reviewableId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    reviewable(id: $reviewableId) {
      ... on ReviewableInterface {
        id
        authors {
          id
          handle
          name
          avatarUrl
        }
        parentReviewLifecycles {
          id
          currentVersion {
            id
            name
            lifecycleType
            reviewStages {
              id
              name
              reviewerType
              selectedTeam {
                id
              }
              isTeamReassignable
            }
          }
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function ReviewStages(props: {
  showNumbers: boolean;
  children: Array<React.ReactElement<StepperStepProps>>;
}): JSX.Element {
  if (props.showNumbers) {
    return <Stepper>{props.children}</Stepper>;
  }
  return (
    <div>
      {React.Children.toArray(props.children).map((step: React.ReactElement<StepperStepProps>) => (
        <FormGroup className={css(styles.formGroup)} key={step.key}>
          <Label className={css(stepperStyles.sectionTitle, styles.label)}>{step.props.title}</Label>
          {step.props.children}
        </FormGroup>
      ))}
    </div>
  );
}

function hasAllReviewers(reviewerUserByStageId: EntityByStageId, stageIds: Array<string>): boolean {
  return stageIds.every((stageId) => reviewerUserByStageId[stageId] != null);
}

function PresentationalSendForReviewModal({
  reviewableId,
  reviewable: {authors, parentReviewLifecycles},
  existingReviewerUsersByStage,
  existingCanTeamReassignByStage,
  onRequestClose,
  onRequestSubmit,
  defaultComment = '',
  renderAdditionalReviewInfo,
  renderReviewError,
  performanceMetadata,
}: PresentationalSendForReviewModalProps): JSX.Element {
  // If no authors are set on the review, assume current user is author.
  authors = authors.length > 0 ? authors : [EntityStore.currentUser()];
  const selectableReviewLifecycles = parentReviewLifecycles.filter(
    ({currentVersion: {lifecycleType}}) => lifecycleType !== ReviewLifecycleType.SELF_REVIEW
  );

  assert(
    selectableReviewLifecycles.length >= 1,
    'Non self-review review lifecycles not present for this project. This modal should not be rendered for entries that cannot be sent for review.'
  );
  const [comment, setComment] = useState<string>(defaultComment);
  const [selectedReviewLifecycle, setSelectedReviewLifecycle] = useState(selectableReviewLifecycles[0]);

  const {reviewStages, lifecycleType} = selectedReviewLifecycle.currentVersion;
  // This will store selections for each stage in every selectable review process. This allows users to toggle
  // between review processes and retain their selections.
  const [reviewerUserByStageId, setReviewerUserByStageId] = useState<EntityByStageId>(
    existingReviewerUsersByStage ?? {}
  );
  const [canTeamReassignByStageId, setCanTeamReassignByStageId] = useState<IsTeamReassignableByStageId>(
    () => {
      // Initialize with default values based on stage.isTeamReassignable
      const initialValues: IsTeamReassignableByStageId = {};
      reviewStages.forEach((stage) => {
        if (stage.reviewerType === ReviewerType.TEAM) {
          initialValues[stage.id] = stage.isTeamReassignable ?? false;
        }
      });
      return {...initialValues, ...existingCanTeamReassignByStage};
    }
  );
  const [submitInProgress, setSubmitInProgress] = useState<boolean>(false);
  const [reviewError, setReviewError] = useState<JSX.Element | null>(null);

  // We always allow submission of empty comments on review lifecycle based reviews, so we don't check that here.
  const [canSubmit, setCanSubmit] = useState<boolean>(
    hasAllReviewers(
      reviewerUserByStageId,
      reviewStages.map(({id}) => id)
    )
  );

  // Pulling this logic into a useEffect allows child component change handlers to not have to worry about
  // the submit button's disabled status.
  useEffect(() => {
    setCanSubmit(
      hasAllReviewers(
        reviewerUserByStageId,
        reviewStages.map(({id}) => id)
      )
    );
  }, [reviewerUserByStageId, reviewStages]);

  const reviewSender = EntityStore.currentUser()?.id; // currentUser is the review sender

  const rows = reviewStages.map((stage, index) => (
    <StepperStep title={stage.name} key={`${stage.name}-${index}`}>
      <ReviewStageReviewerAssignmentRow
        reviewerType={stage.reviewerType}
        authors={authors}
        selectedTeamId={stage.selectedTeam?.id}
        onChange={(entity) => {
          const updatedReviewerUserByStageId = produce(
            reviewerUserByStageId,
            (draftReviewerUserByStageId) => {
              if (entity == null) {
                delete draftReviewerUserByStageId[stage.id];
              } else {
                draftReviewerUserByStageId[stage.id] = entity;
              }
            }
          );
          setReviewerUserByStageId(updatedReviewerUserByStageId);
        }}
        selectedUser={reviewerUserByStageId[stage.id] ?? null}
        dataTestAttrs={{
          component: 'ReviewStageReviewerAssignmentRow',
          element: 'root',
          key: stage.name,
        }}
        reviewSenderId={reviewSender}
        reviewableId={reviewableId}
      />
      {stage.reviewerType === ReviewerType.TEAM && stage.isTeamReassignable && (
        <CheckboxInput
          id={`enable-team-reassign-${stage.name}-${index}`}
          dataTestAttrs={{
            component: 'ReviewStageReassignableCheckbox',
            element: 'root',
            key: stage.name,
          }}
          className={css(styles.checkboxMargin)}
          checked={canTeamReassignByStageId[stage.id] ?? true} // Default checkbox to true
          label="Allow team members to re-assign stage"
          disabled={stage.reviewerType === ReviewerType.TEAM && false} // TODO(BNCH-113773): Disable this on large team sizes
          onChange={(e) => {
            const updatedCanTeamReassignByStageId = produce(
              canTeamReassignByStageId,
              (draftCanTeamReassignByStageId) => {
                draftCanTeamReassignByStageId[stage.id] = e.target.checked;
              }
            );
            setCanTeamReassignByStageId(updatedCanTeamReassignByStageId);
          }}
        />
      )}
    </StepperStep>
  ));

  return (
    <Modal
      closeable={true}
      title="Send for review"
      showFooterBorder={false}
      onRequestClose={onRequestClose}
      onRequestSubmit={async () => {
        const metadata = getSendForReviewMetadata(reviewableId, performanceMetadata);
        logProfilerTimingEvent(SEND_FOR_REVIEW_STARTED_EVENT, metadata);

        setSubmitInProgress(true);
        setReviewError(null);
        try {
          await onRequestSubmit({
            comment,
            stageReviewers: reviewStages.map((stage, index) => ({
              stageId: stage.id,
              reviewerUserId: reviewerUserByStageId[stage.id]!.id,
              isTeamReassignable:
                // Only include the checkbox value when the stage allows team reassignment
                stage.isTeamReassignable && canTeamReassignByStageId[stage.id] !== undefined
                  ? canTeamReassignByStageId[stage.id]
                  : false,
            })),
          });
        } catch (e) {
          const reviewErrorElem = renderReviewError != null ? renderReviewError(e) : null;
          if (reviewErrorElem != null) {
            setReviewError(reviewErrorElem);
          } else {
            throw e;
          }
        } finally {
          setSubmitInProgress(false);
          logProfilerTimingEvent(SEND_FOR_REVIEW_ENDED_EVENT, metadata);
        }
      }}
      buttons={[
        {
          text: 'Send for review',
          submit: true,
          style: ButtonStyle.SECONDARY,
          disabled: submitInProgress || !canSubmit,
        },
      ]}
    >
      <EsignatureStatement action={ReviewAction.SEND_FOR_REVIEW} buttonText="Send for review" />
      {selectableReviewLifecycles.length > 1 && (
        <ReviewProcessSelector
          includeAuditorBasedReviewOption={false}
          className={css(styles.formGroup)}
          labelClassName={css(styles.reviewProcessSelectorLabel)}
          isMulti={false}
          showWarnings={false}
          selectReviewLifecycleId={(reviewLifecycleId): void => {
            const selected = selectableReviewLifecycles.find((rlc) => rlc.id === reviewLifecycleId);
            assert(selected != null, '');
            setSelectedReviewLifecycle(selected);
          }}
          options={generateReviewProcessSelectorOptions({
            reviewLifecycleIdAndNames: selectableReviewLifecycles.map(({id, currentVersion: {name}}) => ({
              id,
              name,
            })),
            selectedReviewLifecycleIds: [selectedReviewLifecycle.id],
            isMulti: false,
            // This allows us to render the dropdown in the same ordering that the project admin chose.
            sortByName: false,
          })}
          selectedReviewLifecycleIds={[selectedReviewLifecycle.id]}
        />
      )}
      {rows}
      <ReviewStages showNumbers={lifecycleType === ReviewLifecycleType.SEQUENTIAL}>{rows}</ReviewStages>
      {renderAdditionalReviewInfo != null
        ? renderAdditionalReviewInfo(reviewableId, ReviewAction.SEND_FOR_REVIEW)
        : null}
      <FormGroup className={css(lifecycleType === ReviewLifecycleType.SEQUENTIAL && styles.indentLeft)}>
        <ReviewComment
          comment={comment}
          onChange={(e: TextAreaChangeEvent) => {
            setComment(e.target.value);
          }}
          labelClassName={css(stepperStyles.sectionTitle, styles.label)}
          rowCount={5}
          disabled={false}
          shouldAutoFocus={false}
          showRedAsterisk={false}
        />
      </FormGroup>
      {reviewError}
    </Modal>
  );
}

export function SendForReviewModal({
  reviewableId,
  onRequestClose,
  onRequestSubmit,
  existingReviewerUsersByStage,
  existingCanTeamReassignByStage,
  defaultComment,
  renderAdditionalReviewInfo,
  renderReviewError,
  performanceMetadata,
}: SendForReviewModalProps): JSX.Element | null {
  const result = useBenchlingQuery<ReviewLifecycleStageQuery, ReviewLifecycleStageQueryVariables>(
    REVIEW_LIFECYCLE_STAGE_QUERY,
    {
      variables: {reviewableId},
      fetchPolicy: 'network-only',
    }
  );

  if (result.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR) {
    return null;
  }

  const {reviewable} = result.data;
  if (isInaccessible(reviewable)) {
    assertAndContinue(false, 'User did not have access to the entry while fetching review data');
    return null;
  }

  // Separating out the Presentational component allows us to only store relevant state if the data is already
  // loaded in.
  return (
    <PresentationalSendForReviewModal
      onRequestClose={onRequestClose}
      onRequestSubmit={onRequestSubmit}
      existingReviewerUsersByStage={existingReviewerUsersByStage}
      existingCanTeamReassignByStage={existingCanTeamReassignByStage}
      reviewable={reviewable}
      defaultComment={defaultComment}
      reviewableId={reviewableId}
      renderAdditionalReviewInfo={renderAdditionalReviewInfo}
      renderReviewError={renderReviewError}
      performanceMetadata={performanceMetadata}
    />
  );
}

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 30,
  },
  reviewProcessSelectorLabel: {
    color: NEAR_BLACK,
    fontSize: 15,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    marginBottom: 15,
  },
  indentLeft: {
    marginLeft: 30,
  },
  label: {
    marginBottom: 15,
  },
  checkboxMargin: {
    marginTop: 8,
  },
});
